import * as _ from 'lodash';

import {
  GetDailyTimeToPayTimeDiscrepancies,
  GetDiscrepancies, GetFlatUkWorkShiftsDiscrepancies,
  GetFlatWorkShiftsDiscrepancies, GetUkDiscrepancies,
  HasPayPunchFields,
  MapPunchToPunchValues
} from '../utilities/timecard';
import {Discrepancy, IndividualDiscrepancyDetails, ProcessedMobileShift} from '../timecard.model';
import {DiscrepancyDetails} from '../../server/external-api/models';
import {ShiftDates, UsaPunchValues, UsaShifts} from '../timecard.shifts.model';
import {ShiftUnit, UkShifts} from '../timecard-models/uk.model';

export interface FlatWorkShifts {
  [workDate: string]: WorkShift;
}

export interface FlatUkWorkShifts {
  [workDate: string]: WorkShift & { units: ShiftUnit };
}
export interface WorkShift {
  workDate: string;
  punches: UsaPunchValues;
  dayType1: string;
  isSplitShift?: boolean;
}
export interface  PossibleShift {
  splitShiftOrdinal: number;
  date: string;
}

const _filterProcessedMobileShifts = (possibleShift: PossibleShift[], processedMobileShifts: ProcessedMobileShift[], mobileShiftDates: ShiftDates) => {
  const processedShift = _
    .chain(mobileShiftDates)
    .values()
    .filter(item => item?.mobileTimeId && _.findIndex(processedMobileShifts, (processedShifts) => {
      return processedShifts.mobiletimeId  === item.mobileTimeId;
    }) > -1)
    .map((shift) => {
      return {date: shift.date, splitShiftOrdinal: shift.splitShiftOrdinal}; }
    )
    .value();
  return _.filter(possibleShift, (shift) => !_.some(processedShift, shift));
};

export const GetAllPossibleShifts = (payTimeShiftDates): PossibleShift[] => _
  .chain(payTimeShiftDates)
  .values()
  .map('date')
  .uniq()
  .map((date) => [{date, splitShiftOrdinal: 0}, {date, splitShiftOrdinal: 1}])
  .flatten()
  .value();
export const GetShiftDiscrepancies = (payTimes: UsaShifts,
                                      dt: UsaShifts, mt: UsaShifts, processedMobileShifts: ProcessedMobileShift[]): IndividualDiscrepancyDetails => {
  // logger.info(`[CalculateShiftDiscrepancies]: Inputs: ${JSON.stringify({payTimes, dt, mt})}`);
  const allPossibleShifts = GetAllPossibleShifts(payTimes?.shiftDates);
  // Filter out processed Shits from all Possible Shifts
  const allPossibleMobileShifts = _.isEmpty(mt) || _.isEmpty(processedMobileShifts)
                                    ? allPossibleShifts
                                    : _filterProcessedMobileShifts(allPossibleShifts, processedMobileShifts, mt.shiftDates);
  // STF-15653 - Assume dt and mt are just a single Shifts and not a Shifts[].  We won't be changing the signature or code that generated
  //  the dt and mt Shifts[].  We will deal with the refactoring later.
  // Create DailyTime Punch that links Punches, day type, pay type and Work Date
  const dailyTimeShifts: FlatWorkShifts = MapShiftsToFlatWorkShifts(dt, allPossibleShifts);
  const mobileTimeShifts: FlatWorkShifts = MapShiftsToFlatWorkShifts(mt, allPossibleMobileShifts);
  /**
   * If times is entered in NDB/NDD End fields and NDB/NDD checkbox is not checked, automatically check it.
   * This needs to done before calculating discrepancy or else if dailyTimes/mobileTimes checkboxes are checked, it will show discrepancy
   */
  const payTimeShifts: FlatWorkShifts = _.reduce(MapShiftsToFlatWorkShifts(payTimes, allPossibleShifts), (result, {punches, ...rest}, key) => {
    result[key] = {
      ...rest,
      punches: {
        ...punches,
        ndb1Out: (!punches?.ndb1Out && punches?.ndb1In) ? 0.5 : punches?.ndb1Out,
        ndb2Out: (!punches?.ndb2Out && punches?.ndb2In) ? 0.5 : punches?.ndb2Out
      }
    };
    return result;
  }, {});
  // pay time to mobile time comparison
  const hasMobilePunches = HasPunches(mobileTimeShifts);
  const hasDailyPunches = HasPunches(dailyTimeShifts);
  const hasMobileWorkDetails = HasWorkDetails(mobileTimeShifts);
  const hasDailyWorkDetails = HasWorkDetails(dailyTimeShifts);
  const payTimeToMobileTimeDiscrepancies: Discrepancy[] = (hasMobilePunches || hasMobileWorkDetails)
    ? GetFlatWorkShiftsDiscrepancies(payTimeShifts, mobileTimeShifts, GetDiscrepancies)
    : [];
  const dailyTimeToPayTimeDiscrepancies: Discrepancy[] = (hasDailyPunches || hasDailyWorkDetails)
    ? GetFlatWorkShiftsDiscrepancies(payTimeShifts, dailyTimeShifts, GetDailyTimeToPayTimeDiscrepancies)
    : [];
  const dailyTimeToMobileTimeDiscrepancies: Discrepancy[] = (hasDailyPunches || hasDailyWorkDetails) && (hasMobilePunches || hasMobileWorkDetails)
    ? GetFlatWorkShiftsDiscrepancies(dailyTimeShifts, mobileTimeShifts, GetDiscrepancies)
    : [];
  return {
    payTimeToMobileTimeDiscrepancies,
    dailyTimeToPayTimeDiscrepancies,
    dailyTimeToMobileTimeDiscrepancies,
    hasMobileTimeDiscrepancy: (hasMobilePunches || hasMobileWorkDetails)
      ? !_.isEmpty(_.filter(payTimeToMobileTimeDiscrepancies, ({entityId}) => !_.isUndefined(entityId)))
      : false
  };
};

export const GetUkShiftDiscrepancies = (payTimes: UkShifts, mt: UkShifts, processedMobileShifts: ProcessedMobileShift[]): IndividualDiscrepancyDetails => {
  // logger.info(`[CalculateShiftDiscrepancies]: Inputs: ${JSON.stringify({payTimes, dt, mt})}`);
  const allPossibleShifts = GetAllPossibleShifts(payTimes?.shiftDates);
  // Filter out processed Shits from all Possible Shifts
  const allPossibleMobileShifts = _.isEmpty(mt) || _.isEmpty(processedMobileShifts)
    ? allPossibleShifts
    : _filterProcessedMobileShifts(allPossibleShifts, processedMobileShifts, mt.shiftDates);
  // STF-15653 - Assume dt and mt are just a single Shifts and not a Shifts[].  We won't be changing the signature or code that generated
  //  the dt and mt Shifts[].  We will deal with the refactoring later.
  // Create DailyTime Punch that links Punches, day type, pay type and Work Date
  const mobileTimeShifts: FlatUkWorkShifts = MapUkShiftsToFlatWorkShifts(mt, allPossibleMobileShifts);
  /**
   * If times is entered in NDB/NDD End fields and NDB/NDD checkbox is not checked, automatically check it.
   * This needs to done before calculating discrepancy or else if dailyTimes/mobileTimes checkboxes are checked, it will show discrepancy
   */
  const payTimeShifts: FlatUkWorkShifts = _.reduce(MapUkShiftsToFlatWorkShifts(payTimes, allPossibleShifts), (result, {punches, ...rest}, key) => {
    result[key] = {
      ...rest,
      punches: {
        ...punches,
        ndb1Out: (!punches?.ndb1Out && punches?.ndb1In) ? 0.5 : punches?.ndb1Out,
        ndb2Out: (!punches?.ndb2Out && punches?.ndb2In) ? 0.5 : punches?.ndb2Out
      }
    };
    return result;
  }, {});
  // pay time to mobile time comparison
  const hasMobilePunches = HasPunches(mobileTimeShifts);
  const hasMobileWorkDetails = HasWorkDetails(mobileTimeShifts);
  const payTimeToMobileTimeDiscrepancies: Discrepancy[] = (hasMobilePunches || hasMobileWorkDetails)
    ? GetFlatUkWorkShiftsDiscrepancies(payTimeShifts, mobileTimeShifts, GetUkDiscrepancies)
    : [];
  return {
    payTimeToMobileTimeDiscrepancies,
    dailyTimeToPayTimeDiscrepancies: [],
    dailyTimeToMobileTimeDiscrepancies: [],
    hasMobileTimeDiscrepancy: (hasMobilePunches || hasMobileWorkDetails)
      ? !_.isEmpty(_.filter(payTimeToMobileTimeDiscrepancies, ({entityId}) => !_.isUndefined(entityId)))
      : false
  };
};

export const CalculateShiftDiscrepancies = (payTimes: UsaShifts,
                                            dt: UsaShifts, mt: UsaShifts, processedMobileShifts: ProcessedMobileShift[]): DiscrepancyDetails => {
  const {payTimeToMobileTimeDiscrepancies, dailyTimeToPayTimeDiscrepancies,
    dailyTimeToMobileTimeDiscrepancies, hasMobileTimeDiscrepancy} = GetShiftDiscrepancies(payTimes, dt, mt, processedMobileShifts);
  const results = {
    discrepancies: _.uniqWith(_.concat(payTimeToMobileTimeDiscrepancies, dailyTimeToPayTimeDiscrepancies, dailyTimeToMobileTimeDiscrepancies),
      (first, second) => first.entityId === second.entityId && first.punchType === second.punchType && first.workDetailsType === second.workDetailsType),
    hasMobileTimeDiscrepancy
  };
  return results;
};
/**
 * MapShiftsToFlatWorkShifts will create the FlatWorkShift that can be used for comparing Daily, Mobile and Pay Time
 *  Creates all possible shifts (14 of them) for DWA Phase2 where we only support Two split shifts per work date.  Filler shifts should have undefined props
 * */
export const MapShiftsToFlatWorkShifts = (shifts: UsaShifts, allPossibleShifts: {date: string, splitShiftOrdinal: number}[]): FlatWorkShifts => {
  if (_.isUndefined(shifts) || _.isNull(shifts)) { return undefined; }
  return _
    .chain(allPossibleShifts)
    .map(({date, splitShiftOrdinal}) => {
      const isSplitShift = _.filter(_.values(shifts.shiftDates), ({date: _date}) => _date === date).length > 1;
      const targetShiftId: string = _
        .chain(shifts.shiftDates)
        .values()
        .find(({date: _date, splitShiftOrdinal: _splitShiftOrdinal = 0}) => _date === date && _splitShiftOrdinal === splitShiftOrdinal)
        .value()
        ?.shiftId;
      const punches = MapPunchToPunchValues(_.get(shifts, `payPunches['${targetShiftId}']`, undefined));
      return {
        workDate: date,
        splitShiftOrdinal,
        dayType1: _.get(shifts, `workDetails['${targetShiftId}'].dayType1.value`, undefined),
        punches: {
          ...punches,
          shiftId: targetShiftId
        },
        isSplitShift
      };
    })
    .keyBy(({workDate, splitShiftOrdinal}) => `${workDate}|${splitShiftOrdinal}`)
    .value();
};
export const MapUkShiftsToFlatWorkShifts = (shifts: UkShifts, allPossibleShifts: {date: string}[]): FlatUkWorkShifts => {
  if (_.isUndefined(shifts) || _.isNull(shifts)) { return undefined; }
  return _
    .chain(allPossibleShifts)
    .map(({date}) => {
      const targetShiftId: string = _
        .chain(shifts.shiftDates)
        .values()
        .find(({date: _date}) => _date === date)
        .value()
        ?.shiftId;
      const punches = MapPunchToPunchValues(_.get(shifts, `punches['${targetShiftId}']`, undefined));
      return {
        workDate: date,
        dayType1: _.get(shifts, `workDetails['${targetShiftId}'].dayType1.value`, undefined),
        punches: {
          ...punches,
          shiftId: targetShiftId
        },
        units: _.get(shifts, `shiftUnits['${targetShiftId}']`)
      };
    })
    .keyBy(({workDate}) => workDate)
    .value();
};
// check if at least 1 shift has punches
const HasPunches = (flatWorkShifts: FlatWorkShifts): boolean => _
  .chain(flatWorkShifts)
  .values()
  .map(({punches}) => punches)
  .filter((punches) => HasPayPunchFields(punches))
  .value().length > 0;
const HasWorkDetails = (flatWorkShifts: FlatWorkShifts): boolean => _
  .chain(flatWorkShifts)
  .values()
  .map(({dayType1}) => !_.isEmpty(dayType1))
  .filter(Boolean)
  .value().length > 0;
